import { useLocale } from 'next-intl'
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetCitiesQuery } from '@/api/cities/get-cities-query'
import { ICity } from '@/api/cities/types/cities-types'
import { IInlinePaginationScrollToRef, InlinePagination } from '@/components/inline-pagination'
import { IListItem, List } from '@/components/list'
import { IModuleSwitchOption, ModuleSwitch } from '@/components/module-switch'
import { useCitiesFinderContext } from '@/features/cities/cities-finder/cities-finder-context'
import { MapLabel } from '@/features/cities/map-label'
import { Locale } from '@/setup/locales'
import { getLinkHref } from '@/setup/routing'

interface ICitiesListProps {
    onAfterSelect?: () => void
    className?: string
    selectedCitySlug?: string
}

interface ICountryCitiesRecord {
    [country: string]: {
        countryEmoji: string | null
        cities: IListItem<ICity>[]
    }
}

export const CitiesList = ({ onAfterSelect, className, selectedCitySlug }: ICitiesListProps) => {
    const { data, isLoading } = useGetCitiesQuery({})

    const allCities = data?.data

    const locale = useLocale()

    const [loadingCitySlug, setLoadingCitySlug] = useState<string | null>(null)

    const onCityClick = useCallback(
        (city: ICity) => {
            setLoadingCitySlug(city.slug)
            onAfterSelect?.()
        },
        [onAfterSelect]
    )

    const countriesRecord = useMemo<ICountryCitiesRecord | undefined>(() => {
        if (!allCities) return

        const sortedCities = [...allCities].sort((a, b) => a.name.localeCompare(b.name))

        const result = sortedCities.reduce<ICountryCitiesRecord>((acc, city) => {
            const cityCountry = city.country
            const cityCountryName = cityCountry?.name

            if (!cityCountry || !cityCountryName) {
                // biome-ignore lint/suspicious/noConsole: data integrity warning
                console.error('City has no country', city)
                return acc
            }

            const prevCountryCities = acc[cityCountryName]?.cities ?? []

            const normalizedNumberOfActiveDeals =
                city.numberOfActiveDeals > 1 ? city.numberOfActiveDeals : 0

            const cityListItem: IListItem<ICity> = {
                href: getLinkHref(
                    {
                        pattern: '/restaurants/[citySlug]',
                        params: { citySlug: city.slug },
                    },
                    locale as Locale
                ),
                onClick: () => onCityClick(city),
                id: city.slug,
                label: (
                    <div className="flex flex-row gap-x-2 items-center">
                        <span>{city.name}</span>
                        <MapLabel label={city.label} />
                    </div>
                ),
                value: city,
                selected: selectedCitySlug === city.slug,
                isLoading: loadingCitySlug === city.slug,
                inlineEndNode: normalizedNumberOfActiveDeals ? (
                    <p className="text-[14px] text-black/50">
                        {normalizedNumberOfActiveDeals} Deals
                    </p>
                ) : null,
            }

            return {
                ...acc,
                [cityCountryName]: {
                    cities: [...(prevCountryCities ?? []), cityListItem],
                    countryEmoji: cityCountry.emoji ?? null,
                },
            }
        }, {} as ICountryCitiesRecord)

        return result
    }, [allCities, selectedCitySlug, loadingCitySlug, locale, onCityClick])

    const countriesOptions = useMemo((): IModuleSwitchOption<string, ReactNode>[] | undefined => {
        if (!countriesRecord) return

        return Object.entries(countriesRecord)
            .sort(
                // sort by number of cities
                ([, { cities: citiesA }], [, { cities: citiesB }]) =>
                    citiesB.length - citiesA.length
            )
            .map(([countryName, { cities, countryEmoji }]) => {
                return {
                    label: (
                        <div>
                            {countryEmoji && (
                                <span className="mr-2" role="img" aria-label={countryName}>
                                    {countryEmoji}
                                </span>
                            )}
                            {countryName}
                            <span className="ml-2 font-medium text-[16px] text-black/40">
                                {cities.length}
                            </span>
                        </div>
                    ),
                    value: countryName,
                }
            })
    }, [countriesRecord])

    const [selectedCountryName, setSelectedCountryName] = useState<string | undefined>(
        () => countriesOptions?.[0].value ?? undefined
    )

    const makeOnSelectCountry = useCallback(
        (scrollToRef?: IInlinePaginationScrollToRef) => (countryName: string) => {
            setSelectedCountryName(countryName)

            scrollToRef?.(countryName)
        },
        []
    )

    // To avoid multiple set-states on the following useEffect, we use a ref to check if the initial state has been synced
    // If the state has a value and is not undefined, we consider it synced.
    const hasSyncedInitialStateRef = useRef(Boolean(selectedCountryName))

    useEffect(() => {
        if (selectedCountryName || !countriesOptions?.length || hasSyncedInitialStateRef.current) {
            return
        }

        hasSyncedInitialStateRef.current = true

        setSelectedCountryName(countriesOptions[0].value ?? undefined)
    }, [selectedCountryName, countriesOptions])

    const list = useMemo<IListItem<ICity>[] | undefined>(() => {
        if (!selectedCountryName || !countriesRecord) return

        return countriesRecord[selectedCountryName]?.cities
    }, [selectedCountryName, countriesRecord])

    const { setHoveredCitySlug } = useCitiesFinderContext()

    const onListItemHover = useCallback(
        (city: ICity) => {
            setHoveredCitySlug(city.slug)
        },
        [setHoveredCitySlug]
    )

    const onListItemHoverEnd = useCallback(() => {
        setHoveredCitySlug(null)
    }, [setHoveredCitySlug])

    // TODO: Empty state
    if (!list?.length) return null

    if (isLoading) return <div className="w-full">Loading...</div>

    return (
        <div className="w-full">
            <InlinePagination
                optionsCount={countriesOptions?.length}
                buttonSize="28px"
                renderChildren={(makeItemRef, scrollToRef) => {
                    return (
                        <ModuleSwitch<string, ReactNode>
                            makeItemRef={makeItemRef}
                            options={countriesOptions ?? []}
                            setSelectedOption={makeOnSelectCountry(scrollToRef)}
                            selectedOptionValue={selectedCountryName}
                            className="w-full"
                        />
                    )
                }}
            />

            <List<ICity>
                onMouseEnter={onListItemHover}
                onMouseLeave={onListItemHoverEnd}
                list={list}
                className={className}
            />
        </div>
    )
}
